import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /** Shows a spinner, disables the button and sets aria-busy. */
  readonly loading?: boolean
  readonly leftIcon?: ReactNode
  readonly rightIcon?: ReactNode
  readonly block?: boolean
}

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-fg border-brand hover:bg-brand-deep hover:border-brand-deep shadow-sm',
  secondary: 'bg-panel text-ink-2 border-line-strong hover:border-ink-2 hover:bg-[#F9FAFD]',
  ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-ink/5',
  danger: 'bg-lost text-white border-lost hover:bg-[#a53f32] hover:border-[#a53f32]',
  link: 'bg-transparent text-brand border-transparent underline-offset-4 hover:underline px-0',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[12.5px] gap-1.5',
  md: 'h-9 px-3.5 text-[13px] gap-2',
  lg: 'h-[44px] px-5 text-[14px] gap-2', // 44 px tap target regardless of the 14 px root font
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    block,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-sm border font-semibold whitespace-nowrap transition-colors select-none',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — icon-only controls must have an accessible name. */
  readonly label: string
  readonly size?: ButtonSize
  readonly variant?: Extract<ButtonVariant, 'secondary' | 'ghost' | 'danger'>
}

const ICON_SIZE: Record<ButtonSize, string> = { sm: 'size-8', md: 'size-9', lg: 'size-[44px]' }

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', variant = 'secondary', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-sm border transition-colors disabled:cursor-not-allowed disabled:opacity-55 [&>svg]:size-4',
        VARIANT[variant],
        ICON_SIZE[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})
